// pages/OnboardingHub.jsx
//
// The landing/hub for merchants whose onboarding is incomplete or pending. It is a thin state-router
// over the backend progress/gate model (single source of truth): depending on status it shows an
// "all set" panel, an "under review" panel, an agreement-signing panel, or — for the common
// incomplete case — the full-page guided OnboardingWizard (one step at a time, not the old
// all-on-one-screen checklist). REJECTED / CHANGE_REQUESTED merchants edit and re-submit through the
// same wizard (its Review step re-submits for review).

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import useOnboardingProgress from '../hooks/useOnboardingProgress';
import { signOnboardingAgreement } from '../services/onboardingProgressService';
import OnboardingWizard from '../components/onboarding/wizard/OnboardingWizard';
import { AuroraBackground, SuccessBurst, AnimatedCheck } from '../components/onboarding/OnboardingDecor';

function StatusPill({ status }) {
  const map = {
    STARTED: 'bg-gray-100 text-gray-700',
    IN_PROGRESS: 'bg-blue-100 text-blue-700',
    PENDING_VERIFICATION: 'bg-amber-100 text-amber-800',
    VERIFIED: 'bg-green-100 text-green-700',
    REJECTED: 'bg-red-100 text-red-700',
    CHANGE_REQUESTED: 'bg-orange-100 text-orange-700',
  };
  const label = {
    STARTED: 'Getting started',
    IN_PROGRESS: 'In progress',
    PENDING_VERIFICATION: 'Under review',
    VERIFIED: 'Verified',
    REJECTED: 'Not approved',
    CHANGE_REQUESTED: 'Changes requested',
  };
  const cls = map[status] || 'bg-gray-100 text-gray-700';
  return <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${cls}`}>{label[status] || status || 'Unknown'}</span>;
}

function Shell({ status, wide, children }) {
  return (
    <div className="relative min-h-[calc(100vh-4rem)] overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <AuroraBackground />
      <div className={`relative z-10 mx-auto px-4 ${wide ? 'max-w-5xl py-6 sm:py-8' : 'max-w-4xl py-8 sm:py-12'}`}>
        <div className="mb-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-lg font-extrabold tracking-tight text-gray-900">Wayuvega</span>
            <span className="hidden text-sm text-gray-400 sm:inline">· Account setup</span>
          </div>
          {status && <StatusPill status={status} />}
        </div>
        {children}
      </div>
    </div>
  );
}

function CenteredCard({ tone = 'blue', icon, title, celebrate, children }) {
  const bar = {
    blue: 'from-blue-500 to-indigo-600',
    green: 'from-emerald-500 to-green-600',
    amber: 'from-amber-500 to-orange-600',
    red: 'from-red-500 to-rose-600',
  }[tone];
  return (
    <motion.div
      initial={{ opacity: 0, y: 18, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.45, ease: 'easeOut' }}
      className="relative mx-auto max-w-xl overflow-hidden rounded-3xl border border-gray-100 bg-white/90 shadow-2xl shadow-slate-300/40 backdrop-blur-xl"
    >
      <div className={`relative overflow-hidden bg-gradient-to-r ${bar} px-6 py-7 text-white`}>
        {celebrate && <SuccessBurst />}
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 18, delay: 0.1 }}
          className="relative text-4xl"
          aria-hidden="true"
        >
          {icon}
        </motion.div>
        <h1 className="relative mt-2 text-2xl font-bold">{title}</h1>
      </div>
      <div className="space-y-4 p-6 sm:p-8">{children}</div>
    </motion.div>
  );
}

export default function OnboardingHub() {
  const merchantId = useSelector((s) => s.auth?.merchantId) || localStorage.getItem('merchantId') || null;
  const navigate = useNavigate();
  const location = useLocation();
  const justSignedUp = !!location.state?.justSignedUp;

  const {
    status, completionPercent, businessOpsUnlocked,
    agreementRequired, agreementSigned, isLoading, isError, refetch,
  } = useOnboardingProgress();

  const [signing, setSigning] = useState(false);
  const [signError, setSignError] = useState(null);

  const handleSign = async () => {
    if (!merchantId) { setSignError('We could not determine your merchant account. Please refresh and try again.'); return; }
    setSigning(true); setSignError(null);
    try {
      await signOnboardingAgreement(merchantId);
      await refetch();
    } catch (err) {
      setSignError(err?.response?.data?.message || err?.message || 'Failed to sign the agreement. Please try again.');
    } finally {
      setSigning(false);
    }
  };

  if (isLoading) {
    return (
      <Shell>
        <div className="animate-pulse space-y-4">
          <div className="h-10 w-1/2 rounded bg-gray-200" />
          <div className="h-72 rounded-3xl bg-gray-100" />
        </div>
      </Shell>
    );
  }

  if (isError) {
    return (
      <Shell>
        <CenteredCard tone="red" icon="⚠️" title="Couldn’t load your status">
          <p className="text-gray-600">We couldn’t load your onboarding status right now.</p>
          <button onClick={() => refetch()} className="rounded-xl bg-blue-600 px-5 py-2.5 font-semibold text-white hover:bg-blue-700">Retry</button>
        </CenteredCard>
      </Shell>
    );
  }

  const agreementPending = agreementRequired && !agreementSigned;
  const safePercent = Math.max(0, Math.min(100, completionPercent || 0));

  // 1) Fully activated.
  if (businessOpsUnlocked) {
    return (
      <Shell status={status}>
        <CenteredCard tone="green" celebrate icon={<AnimatedCheck size={52} className="text-white" />} title="You’re all set!">
          <p className="text-gray-600">Your account is active. You can create orders and start shipping right away.</p>
          <button
            type="button" onClick={() => navigate('/all-shipments')}
            className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-emerald-600 to-green-600 px-5 py-2.5 font-semibold text-white shadow-md hover:from-emerald-700 hover:to-green-700"
          >
            Go to dashboard →
          </button>
        </CenteredCard>
      </Shell>
    );
  }

  // 2) Agreement pending.
  if (agreementPending) {
    return (
      <Shell status={status}>
        <CenteredCard tone="amber" icon="✍️" title="Review & sign your agreement">
          <p className="text-gray-600">Your merchant agreement is ready. You can view the document from your <a href="/profile" className="font-semibold text-blue-600 hover:underline">Profile → Documents</a>, then sign to activate your account.</p>
          {signError && <p className="text-sm text-red-600">{signError}</p>}
          <button
            type="button" onClick={handleSign} disabled={signing}
            className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-2.5 font-semibold text-white shadow-md hover:from-blue-700 hover:to-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {signing ? 'Signing…' : 'I have reviewed and agree — Sign'}
          </button>
        </CenteredCard>
      </Shell>
    );
  }

  // 3) Submitted, awaiting verification.
  if (status === 'PENDING_VERIFICATION') {
    return (
      <Shell status={status}>
        <CenteredCard tone="blue" celebrate icon="⏳" title="Your account is under review">
          <p className="text-gray-600">We’re verifying your details and will notify you by email and in-app once approved — usually within a few hours. No action is needed right now.</p>
          <button
            type="button" onClick={() => navigate('/all-shipments')}
            className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-2.5 font-semibold text-white shadow-md hover:from-blue-700 hover:to-indigo-700"
          >
            Explore the dashboard →
          </button>
        </CenteredCard>
      </Shell>
    );
  }

  // 4) Incomplete (STARTED / IN_PROGRESS) or needs re-apply (REJECTED / CHANGE_REQUESTED) → guided wizard.
  const reapply = status === 'REJECTED' || status === 'CHANGE_REQUESTED';
  const heading = reapply ? 'Update & re-apply' : 'Let’s finish your setup';
  const subheading = reapply
    ? (status === 'REJECTED' ? 'Update the flagged details and re-submit.' : 'Make the requested changes and re-submit.')
    : `${safePercent}% done — saved automatically.`;
  return (
    <Shell status={status} wide>
      <OnboardingWizard
        variant="page"
        heading={heading}
        subheading={subheading}
        welcome={justSignedUp && !reapply}
        onExit={() => navigate('/all-shipments')}
        onComplete={() => refetch()}
      />
    </Shell>
  );
}
